import { Command, Option } from "clipanion";
import { confirm } from "@inquirer/prompts";
import { M8tCommand } from "../../lib/m8t-command.js";
import { resolveGatewayContext } from "../../lib/gateway-context.js";
import { runAz } from "../../lib/az.js";
import { LocalCliError } from "../../lib/errors.js";
import {
  DEFAULT_IMAGE_REPO,
  fetchPublicTags,
  parseContainerAppResourceId,
  planUpdate,
  summarizePlan,
} from "../../lib/platform-update.js";
import { colors, renderJson, renderKeyValueBlock, resolveOutputMode } from "../../lib/output.js";

export class PlatformUpdateCommand extends M8tCommand {
  static paths = [["platform", "update"]];
  static usage = Command.Usage({
    description: "Roll the gateway to the newest published platform image.",
    details:
      "Discovers the live gateway, compares its image tag to the newest vX.Y.Z on the public GHCR repo, and (if newer) rolls a new Container Apps revision via 'az containerapp update --image'. A fresh tag is required to roll in single-revision mode. Use --check to preview, --to to pin a version, --yes to skip the prompt.",
  });

  subscription = Option.String("--subscription");
  imageRepo = Option.String("--image-repo", DEFAULT_IMAGE_REPO);
  to = Option.String("--to");
  resourceGroup = Option.String("--resource-group", {
    description: "m8t-stack resource group to disambiguate the gateway (multi-deployment subscriptions).",
  });
  check = Option.Boolean("--check", false);
  yes = Option.Boolean("--yes", false);
  output = Option.String("--output");

  async executeCommand(): Promise<number> {
    const mode = resolveOutputMode(
      this.output as "pretty" | "json" | "auto" | undefined,
      this.context.stdout as NodeJS.WriteStream,
    );
    const interactive = (this.context.stdout as NodeJS.WriteStream).isTTY === true;
    const log = (msg: string) => {
      if (mode !== "json") this.context.stdout.write(msg + "\n");
    };

    const ctx = await resolveGatewayContext({
      interactive: mode !== "json",
      subscriptionId: this.subscription,
      resourceGroup: this.resourceGroup,
    });
    const { resourceGroup, name } = parseContainerAppResourceId(ctx.containerAppResourceId);
    const currentImage = (
      await runAz([
        "containerapp", "show", "-g", resourceGroup, "-n", name,
        "--query", "properties.template.containers[0].image", "-o", "tsv",
      ])
    ).trim();
    const tags = await fetchPublicTags(this.imageRepo);
    const plan = planUpdate({
      currentImage,
      availableTags: tags,
      imageRepo: this.imageRepo,
      to: this.to,
    });
    const s = summarizePlan(plan);

    if (plan.kind === "refuse-byoc") {
      throw new LocalCliError({
        code: "PLATFORM_BYOC_NOT_TRACKED",
        message: `This gateway runs a private-ACR image (${plan.currentRepo}), not the tracked public repo (${plan.trackedRepo}).`,
        hint: "BYOC instances update by rebuilding + pushing your own image, then 'm8t deploy --image-ref <your-acr>'. Use --image-repo to track a different public repo.",
      });
    }
    if (plan.kind === "tag-not-found") {
      throw new LocalCliError({
        code: "PLATFORM_TAG_NOT_FOUND",
        message: `Tag ${plan.requested} is not published on ${this.imageRepo}.`,
        hint: "Run 'm8t platform status' to see the newest available version, or omit --to.",
      });
    }
    if (plan.kind === "no-versions") {
      throw new LocalCliError({
        code: "PLATFORM_NO_VERSIONS",
        message: `No published vX.Y.Z tags found on ${this.imageRepo}.`,
        hint: "Publish one via the release-image workflow, or check --image-repo.",
      });
    }

    if (mode !== "json") {
      this.context.stdout.write(
        renderKeyValueBlock([
          { key: "gateway", value: name },
          { key: "current", value: s.current },
          { key: "available", value: s.available },
          { key: "status", value: s.verdict },
        ]) + "\n",
      );
    }

    if (plan.kind === "noop") {
      if (mode === "json") this.context.stdout.write(renderJson({ ...s, kind: plan.kind, rolled: false }) + "\n");
      else log(colors.success("already up to date ✓"));
      return 0;
    }

    // plan.kind === "roll"
    if (this.check) {
      if (mode === "json") this.context.stdout.write(renderJson({ ...s, kind: plan.kind, rolled: false, wouldRollTo: plan.target }) + "\n");
      else log(colors.dim(`(--check) would roll to ${plan.target}`));
      return 0;
    }

    if (!this.yes && interactive) {
      const ok = await confirm({
        message: `Roll ${name} from ${plan.current} to ${plan.target}?`,
        default: true,
      });
      if (!ok) {
        log(colors.dim("aborted — no change."));
        return 0;
      }
    }

    log(`rolling to ${plan.target}…`);
    await runAz([
      "containerapp", "update", "-n", name, "-g", resourceGroup,
      "--image", `${plan.repo}:${plan.target}`,
    ]);

    if (mode === "json") this.context.stdout.write(renderJson({ ...s, kind: plan.kind, rolled: true, rolledTo: plan.target }) + "\n");
    else log(colors.success(`rolled ${plan.current} → ${plan.target} ✓`));
    return 0;
  }
}
